/**
 * web-account-pages D3 — one live chat per tab (`WebChatHub`), held by WebLayout and read by the
 * header's Messages button, the messages panel and the message banner. It runs while a client is
 * signed in and stops, dropping everything it read, on sign-out or when another person signs in.
 */
import { createContext, useContext, useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';

import {
  fetchWebChatUnread,
  fetchWebThread,
  markWebChatRead,
  postWebTyping,
  readWebChatEvents,
  sendWebMessage,
} from '../api/chat';
import { useWebSession } from '../api/session';
import { WebChatHub, type ChatTransport, type WebChatState } from './chatHub';
import { useMessagesPanel } from './panelContext';

const transport: ChatTransport = {
  fetchThread: fetchWebThread,
  fetchUnread: fetchWebChatUnread,
  markRead: markWebChatRead,
  send: sendWebMessage,
  postTyping: postWebTyping,
  readEvents: readWebChatEvents,
};

const HubContext = createContext<WebChatHub | null>(null);

export function WebChatProvider({ children }: { readonly children: ReactNode }) {
  const [hub] = useState(() => new WebChatHub(transport));
  const session = useWebSession();
  const userId = session.status === 'signedIn' ? (session.user?.id ?? null) : null;
  const { open } = useMessagesPanel();

  useEffect(() => {
    if (userId) hub.start(userId);
    else if (session.status === 'signedOut') hub.stop();
  }, [hub, session.status, userId]);
  useEffect(() => () => hub.stop(), [hub]);
  useEffect(() => hub.setPanelOpen(open), [hub, open]);

  return <HubContext.Provider value={hub}>{children}</HubContext.Provider>;
}

const idleHub = new WebChatHub(transport);

/** The tab's chat and its state. Outside WebLayout: an idle hub that reads nothing. */
export function useWebChat(): { readonly hub: WebChatHub; readonly state: WebChatState } {
  const hub = useContext(HubContext) ?? idleHub;
  const state = useSyncExternalStore(hub.subscribe, hub.getState);
  return { hub, state };
}
